import {
  Button,
  ButtonGroup,
  Css,
  ModalProps,
  RightPaneLayout,
  ScrollableContent,
  Switch,
  useModal,
} from "@homebound/beam";
import { useState } from "react";
import { useNavigate } from "react-router";
import type { NavigateFunction } from "react-router-dom";
import { SearchBox } from "src/components";
import { showPBBv2AcceptUpdates } from "src/features";
import { BidPackageStatus } from "src/generated/graphqlTypes";
import { PageBidPackageDetailFragment } from "src/routes/bids/BidPackageDetailPage.generated";
import { PlanBasedBidsTableV2 } from "src/routes/bids/plan-based-bid-package/components/PlanBasedBidsTableV2";
import { PlanBasedBidsTableV2_BidPackageBidderFragment } from "src/routes/bids/plan-based-bid-package/components/PlanBasedBidsTableV2.generated";
import { TableActions } from "src/routes/layout/TableActions";
import { createEnterPlanBasedBidUrl } from "src/RouteUrls";
import { pushInNextLoop } from "src/utils";
import { SelectBidderModal } from "../plan-based-bid-package/components/SelectBidderModal";
import { UploadPlanBasedBidV2Modal } from "../plan-based-bid-package/components/UploadPlanBasedBidV2Modal";
import { AddTradePartnerModal } from "./AddTradePartnerModal";
import { PlanBasedBidsTable } from "./PlanBasedBidsTable";
import { UnitBasedBidsTable } from "./UnitBasedBidsTable";
import { getBidPackageTradesWithEstimate } from "./utils";

type BidsTabProps = { bidPackage: PageBidPackageDetailFragment };

export function BidsTab(props: BidsTabProps) {
  const { bidPackage } = props;
  const { openModal } = useModal();
  const navigate = useNavigate();
  const [searchFilter, setSearchFilter] = useState<string | undefined>("");
  const [showPlanUsage, setShowPlanUsage] = useState(!bidPackage.isUnitBased);
  const hasUnacceptedChanges = showPBBv2AcceptUpdates && bidPackage.availableUpdates.nonEmpty;
  const [scopeView, setScopeView] = useState<"hide" | "all" | "changes">(hasUnacceptedChanges ? "changes" : "hide");

  // Get all trades without contracts or with draft contracts
  const trades = getBidPackageTradesWithEstimate(bidPackage);
  const requestsWithEditableBids = trades.filter((trade) => {
    // If the bid package is awarded, we don't allow edits
    if (bidPackage.status.code === BidPackageStatus.Awarded) {
      return false;
    }
    const bidContractRevision = trade.bidContractRevision;
    return !bidContractRevision || bidContractRevision.canEdit.allowed;
  });

  return (
    <>
      <TableActions>
        <div css={Css.df.aic.gap2.$}>
          <SearchBox onSearch={setSearchFilter} />
          {bidPackage.isPlanBasedV2 && (
            <div css={Css.df.aic.gap1.$}>
              <span>View: </span>
              <ButtonGroup
                size="md"
                buttons={[
                  { text: "Hide Scope", onClick: () => setScopeView("hide"), active: scopeView === "hide" },
                  { text: "All Scope", onClick: () => setScopeView("all"), active: scopeView === "all" },
                  ...(hasUnacceptedChanges
                    ? [
                        {
                          text: "Changes Only",
                          onClick: () => setScopeView("changes"),
                          active: scopeView === "changes",
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}
        </div>
        {bidPackage.isUnitBased && (
          <Switch label="Show Plan Usage" selected={showPlanUsage} onChange={setShowPlanUsage} />
        )}
        {bidPackage.isPlanBasedV2 && (
          <div css={Css.df.gap1.$}>
            <Button
              label="Enter Bid"
              onClick={() => goToEnterPlanBasedBidPage(bidPackage, bidPackage.activeBidders, openModal, navigate)}
              variant="textSecondary"
              icon="pencil"
            />
            <Button
              label="Upload Bid"
              onClick={() => openModal({ content: <UploadPlanBasedBidV2Modal bidPackage={bidPackage} /> })}
              variant="textSecondary"
              icon="upload"
            />
          </div>
        )}
      </TableActions>
      <div css={Css.h100.$}>
        {!showPlanUsage ? (
          // Since the plan based table has tabs the scrollable content is wrapped inside that component
          <ScrollableContent virtualized>
            <RightPaneLayout>
              <div css={Css.h100.$}>
                <UnitBasedBidsTable
                  bidPackage={bidPackage}
                  searchFilter={searchFilter}
                  requestsWithEditableBids={requestsWithEditableBids}
                />
              </div>
            </RightPaneLayout>
          </ScrollableContent>
        ) : bidPackage.isPlanBasedV2 ? (
          <PlanBasedBidsTableV2 bidPackage={bidPackage} searchFilter={searchFilter} scopeView={scopeView} />
        ) : (
          <PlanBasedBidsTable
            bidPackage={bidPackage}
            searchFilter={searchFilter}
            requestsWithEditableBids={requestsWithEditableBids}
          />
        )}
      </div>
    </>
  );
}

export function getOptionsKey(opt: { __typename?: "ReadyPlanOption" | undefined; id: string }[]) {
  return opt
    .sortByKey("id")
    .map((o) => o.id)
    .join(",");
}

/**
 * Before going to the enter plan based bid page, route based on how many bidders exist:
 * - 0 bidders: open the add-trade-partner modal (both trade partners and internal estimate can be added through this flow).
 * - 1 bidder: go straight to the enter-bid page.
 * - 2+ bidders: open the select-bidder modal so the user picks (trade partner or internal estimate).
 */
function goToEnterPlanBasedBidPage(
  bidPackage: PageBidPackageDetailFragment,
  bidders: PlanBasedBidsTableV2_BidPackageBidderFragment[],
  openModal: (props: ModalProps) => void,
  navigate: NavigateFunction,
) {
  const goToEnterPlanBasedBid = (bidderId: string) => {
    pushInNextLoop(navigate, createEnterPlanBasedBidUrl(bidPackage.id, bidderId));
  };

  if (bidders.length === 1) {
    goToEnterPlanBasedBid(bidders[0].id);
  } else if (bidders.isEmpty) {
    openModal({ content: <AddTradePartnerModal bidPackage={bidPackage} /> });
  } else {
    openModal({ content: <SelectBidderModal bidders={bidders} onSelect={goToEnterPlanBasedBid} /> });
  }
}
